import React, { useState, useEffect } from 'react'
import { Routes, Route } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { Header } from './components/Header'
import { Footer } from './components/Footer'
import { HomePage } from './components/HomePage'
import { ErrorBoundary } from './components/ErrorBoundary'
import NotFound from './components/NotFound'
import { CookieConsent } from './components/CookieConsent'
import { useFontLoading } from './hooks/useFontLoading'
import { useI18n } from './hooks/useI18n'
import { usePageTracking, useEventTracking, initializeGoogleAnalytics, trackCoreWebVitals } from './hooks/useGoogleAnalytics'
import { Locale } from './lib/dictionary'

// Importar todas las páginas
const ContactoPage = React.lazy(() => import('./pages/ContactoPage'))
const ServiciosPage = React.lazy(() => import('./pages/ServiciosPage'))
const QuienesSomosPage = React.lazy(() => import('./pages/QuienesSomosPage'))
// TEMPORALMENTE OCULTO - Testimonios (será habilitado cuando tengamos información real)
// const TestimoniosPage = React.lazy(() => import('./pages/TestimoniosPage'))
const TrastornosPage = React.lazy(() => import('./pages/TrastornosPage'))
const CondicionesDelDesarrolloPage = React.lazy(() => import('./pages/CondicionesDelDesarrolloPage'))
const NuestrasPruebasPage = React.lazy(() => import('./pages/NuestrasPruebasPage'))
const EspecialistasPage = React.lazy(() => import('./pages/EspecialistasPage'))
const EspecialistaIndividualPage = React.lazy(() => import('./pages/EspecialistaIndividualPage'))
const NeuropsicologiaInfantilPage = React.lazy(() => import('./pages/NeuropsicologiaInfantilPage'))
const PsicologiaInfantilPage = React.lazy(() => import('./pages/PsicologiaInfantilPage'))
const PsicopedagogiaPage = React.lazy(() => import('./pages/PsicopedagogiaPage'))
const TerapiaDeLenguajePage = React.lazy(() => import('./pages/TerapiaDeLenguajePage'))
const TerapiaOcupacionalPage = React.lazy(() => import('./pages/TerapiaOcupacionalPage'))
// TEMPORALMENTE OCULTO - const MedicinaGeneralInfantilPage = React.lazy(() => import('./pages/MedicinaGeneralInfantilPage'))
const PsiquiatriaPediatricaPage = React.lazy(() => import('./pages/PsiquiatriaPediatricaPage'))


function App() {
  const [locale, setLocale] = useState<Locale>('es')
  const i18n = useI18n(locale, setLocale)
  
  // Optimización de carga de fuentes
  const { allFontsLoaded } = useFontLoading()
  
  // Google Analytics
  const measurementId = import.meta.env.VITE_GOOGLE_ANALYTICS_ID
  const { trackEvent } = useEventTracking()
  
  // Inicializar Google Analytics
  useEffect(() => {
    if (measurementId) {
      initializeGoogleAnalytics(measurementId)
      trackCoreWebVitals()
    }
  }, [measurementId])
  
  // Tracking de páginas
  usePageTracking(measurementId)
  
  // Manejar consentimiento de cookies
  const handleCookieConsent = (analytics: boolean, ads: boolean) => {
    if (typeof window.updateConsent === 'function') {
      window.updateConsent(analytics, ads)
    }
    
    // Track consentimiento
    trackEvent('cookie_consent', {
      category: 'privacy',
      label: `analytics:${analytics},ads:${ads}`,
      value: 1
    })
  }

  return (
    <HelmetProvider>
      <ErrorBoundary>
        <div className={`min-h-screen ${allFontsLoaded ? 'fonts-loaded' : 'fonts-loading'}`}>
          {/* Header con navegación */}
          <Header dictionary={i18n.t} />
          
          {/* Contenido principal con enrutamiento */}
          <React.Suspense fallback={<div className="py-10 text-center text-gray-500">{i18n.t.common.loading}</div>}>
            <Routes>
            {/* Página principal */}
            <Route path="/" element={<HomePage dictionary={i18n.t} />} />
            
            {/* Páginas de servicios */}
            <Route path="/servicios" element={<ServiciosPage dictionary={i18n.t} />} />
            <Route path="/servicios/neuropsicologia-infantil" element={<NeuropsicologiaInfantilPage />} />
            <Route path="/servicios/psicologia-infantil" element={<PsicologiaInfantilPage />} />
            <Route path="/servicios/psicopedagogia" element={<PsicopedagogiaPage />} />
            <Route path="/servicios/terapia-de-lenguaje" element={<TerapiaDeLenguajePage />} />
            <Route path="/servicios/terapia-ocupacional" element={<TerapiaOcupacionalPage />} />
            <Route path="/servicios/psiquiatria-pediatrica" element={<PsiquiatriaPediatricaPage />} />
            {/* TEMPORALMENTE OCULTO - Medicina General Infantil
            <Route path="/servicios/medicina-general-infantil" element={<MedicinaGeneralInfantilPage />} />
            */}
            
            {/* Páginas informativas */}
            <Route path="/quienes-somos" element={<QuienesSomosPage dictionary={i18n.t} />} />
            {/* TEMPORALMENTE OCULTO - Testimonios (será habilitado cuando tengamos información real)
            <Route path="/testimonios" element={<TestimoniosPage dictionary={i18n.t} />} />
            */}
                                      <Route path="/trastornos" element={<TrastornosPage dictionary={i18n.t} />} />
            <Route path="/condiciones-del-desarrollo" element={<CondicionesDelDesarrolloPage dictionary={i18n.t} />} />
            <Route path="/nuestras-pruebas" element={<NuestrasPruebasPage dictionary={i18n.t} />} />
            
            {/* Páginas de especialistas */}
            <Route path="/especialistas" element={<EspecialistasPage dictionary={i18n.t} />} />
            <Route path="/especialistas/:id" element={<EspecialistaIndividualPage />} />
            
            {/* Página de contacto */}
            <Route path="/contacto" element={<ContactoPage dictionary={i18n.t} />} />
            

            
            {/* Ruta catch-all para páginas no encontradas */}
            <Route path="*" element={<NotFound dictionary={i18n.t} />} />
            </Routes>
          </React.Suspense>

          {/* Footer */}
          <Footer dictionary={i18n.t} />
          
          {/* Cookie Consent */}
          <CookieConsent onAccept={handleCookieConsent} />
        </div>
      </ErrorBoundary>
    </HelmetProvider>
  )
}

export default App
